import { useCallback, useEffect, useMemo, useState } from 'react';
import { PropertyGrid, TextInput, Pane, Button, Content, Toast } from '@leverege/ui-elements';
import { ItemCard } from '@leverege/ui-attributes';
import { OkayCancelEditor } from '@leverege/ui-plugin';
import { useValueChange } from '@leverege/ui-hooks';
import Editor from '@monaco-editor/react';
import { useSelector } from 'react-redux';
import RsrcModelEditorFactory from "./rsrcModelEditors/RsrcModelEditorFactory.js";
import Model from '../models/Rsrc.js';
import S from './RsrcEditor.css';
import ImagineRsrcGroupDelegate from '../analyzer/ImagineRsrcGroupDelegate.js';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function RsrcEditor(props) {
  const {
    value,
    inContent,
    details,
    width
  } = props;
  const onChange = useValueChange(Model, props);
  const onSetMetdata = useCallback(value => {
    onChange({
      data: 'setMetadataJSON',
      value
    });
  }, [onChange]);
  const onKeyDown = useCallback(evt => {
    evt.stopPropagation();
  }, []);
  if (!value) {
    return null;
  }
  const typeEditorPlg = RsrcModelEditorFactory.get(Model.getRootData(value));
  const editorLocation = typeEditorPlg?.location ?? 'middle';
  const typeEditor = /*#__PURE__*/_jsx(PropertyGrid.Item, {
    fill: true,
    children: RsrcModelEditorFactory.create(Model.getRootData(value), {
      onChange,
      rsrc: value,
      model: Model,
      width,
      eventData: 'setRootData'
    })
  });
  const editor = /*#__PURE__*/_jsxs(PropertyGrid, {
    vertical: true,
    variant: "rsrcEditor",
    className: S.grid,
    onKeyDown: onKeyDown,
    children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: "Path",
      children: /*#__PURE__*/_jsx(TextInput, {
        className: S.pathEditor,
        value: Model.getPath(value),
        eventData: "setPath",
        onChange: onChange
      })
    }), editorLocation === 'top' ? typeEditor : [], /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fill: true,
      label: "Details",
      children: /*#__PURE__*/_jsx(ItemCard, {
        model: details,
        context: {
          id: value.id,
          target: [value]
        }
      })
    }), editorLocation === 'middle' ? typeEditor : [], value.id && /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: "Metadata",
      children: /*#__PURE__*/_jsx(Editor, {
        options: {
          minimap: {
            enabled: false
          }
        },
        height: "400px",
        width: width ? width - 72 : width,
        defaultLanguage: "json",
        value: Model.getMetadataJSON(value),
        onChange: onSetMetdata,
        theme: "vs-dark"
      })
    }), editorLocation === 'bottom' ? typeEditor : []]
  });
  let content;
  if (inContent) {
    content = /*#__PURE__*/_jsx(Content.Area, {
      className: S.contentArea,
      children: editor
    });
  } else {
    content = /*#__PURE__*/_jsx(Content, {
      variant: "rsrcEditor",
      style: {
        width: '100%'
      },
      children: /*#__PURE__*/_jsx(Content.Area, {
        variant: "rsrcEditorArea",
        children: editor
      })
    });
  }
  return content;
}
function RsrcEditorWrapper({
  value,
  onChange: pOnChange,
  ...rest
}) {
  const {
    delegate
  } = rest;
  const onChange = useCallback(evt => {
    const {
      value
    } = evt;
    const metadataJSON = Model.getMetadataJSON(value);
    if (metadataJSON) {
      try {
        JSON.parse(metadataJSON);
      } catch (error) {
        Toast.error(['Invalid JSON', 'Metadata must be valid json. Please correct any errors and try again.'], {
          containerId: 'selectedDialog'
        });
        return;
      }
    }
    pOnChange(evt);
  }, [pOnChange]);
  const objRef = useSelector(state => delegate.item(value.data.path).getItem(state));
  const val = useMemo(() => {
    if (!objRef) {
      return null;
    }
    return Model.create(delegate.item(value.data.path).toObjectRef(objRef));
  }, [objRef, value.data.path]);
  return /*#__PURE__*/_jsx(OkayCancelEditor, {
    variants: {
      pane: 'rsrcEditor'
    },
    editor: RsrcEditor,
    editorProps: rest,
    value: val,
    onChange: onChange,
    cancelLabel: "Revert",
    okayLabel: "Save"
  });
}
RsrcEditorWrapper.PLUGIN = {
  type: 'rsrc',
  editor: RsrcEditorWrapper
};
export default RsrcEditorWrapper;